/* eslint-disable arrow-body-style */
import React, { useRef, useState, useEffect } from 'react';
import { useThrottleFn } from 'ahooks';
import { INodePropCfg, getNodeProps } from '@intelligence/utils/domUtils';
import { getChildrenWidth, getIsOverflow } from '../utils';

const NODE_PROPS: INodePropCfg[] = [
  { name: 'width', type: 'number' },
  { name: 'padding-left', type: 'number' },
  { name: 'padding-right', type: 'number' },
];

// 左右滑动按钮禁用状态
interface ISlideDisabledState {
  prev: boolean;
  next: boolean;
}

function useResizeEffect(props: {
  containerRef: React.RefObject<HTMLDivElement>;
  listContainerRef: React.RefObject<HTMLDivElement>;
  updateIsOverflow: () => void;
  updateSlideDisabledState: (scrollLeft: number) => void;
}) {
  const {
    containerRef,
    listContainerRef,
    updateIsOverflow,
    updateSlideDisabledState,
  } = props;

  const { run: handleResizeRun } = useThrottleFn(() => {
    if (!containerRef.current) return;
    if (!listContainerRef.current) return;

    updateIsOverflow();

    const scrollLeft = listContainerRef.current?.scrollLeft || 0;
    updateSlideDisabledState(scrollLeft);
  }, {
    wait: 600,
    leading: true,
    trailing: true,
  });

  useEffect(() => {
    window.addEventListener('resize', handleResizeRun, false);

    return () => {
      window.removeEventListener('resize', handleResizeRun, false);
    };
  }, []);
}

export default function useSlideEffect(props: {
  // 计算是否超宽溢出时，忽略的超出宽度（即超出宽度小于、等于该值时，判定为非超出宽度），默认为0
  overflowOffset?: number;
  // 宽度超出时，左右滑动按钮宽度，其他非滚动列表容器元素的宽度，默认为0
  othersWidth?: number;
  // 点击左右滑动按钮时，滑动的距离占比，相对与可滑动容器的宽度而言，默认为0.6
  distanceRatio?: number;
  // 重新计算是否超出，各个元素宽度的 更新依赖项
  updateDeps?: any[];
}) {
  const { overflowOffset = 0, othersWidth = 0, distanceRatio = 0.6, updateDeps = [] } = props;

  // 容器dom
  const containerRef = useRef<HTMLDivElement>(null);
  // 列表容器dom
  const listContainerRef = useRef<HTMLDivElement>(null);
  // 容器dom宽度
  const containerWidth = useRef<number>(0);
  // 列表容器dom的宽度
  const listContainerWidth = useRef<number>(0);
  // 列表子元素的总宽度
  const listWidth = useRef<number>(0);
  // 列表子元素超出的宽度（相对与列表容器dom而言）
  const listOverflowWidth = useRef<number>(0);

  // 是否超宽，导致溢出隐藏、滚动
  const [isOverflow, setIsOverflow] = useState(false);
  // 左右滑动按钮禁用状态
  const [slideDisabledState, setSlideDisabledState] = useState<ISlideDisabledState>({
    prev: true,
    next: false,
  });

  // 根据当前scrollLeft 计算 左右滑动按钮的禁用状态
  const getSlideDisabledState = (scrollLeft: number): ISlideDisabledState => {
    return {
      prev: scrollLeft === 0,
      next: scrollLeft >= listOverflowWidth.current,
    };
  };

  // 左滑动
  const handleSlideLeft = () => {
    if (slideDisabledState.prev) return;
    const distance = listContainerWidth.current * distanceRatio;
    const scrollLeft = listContainerRef.current?.scrollLeft || 0;
    const scrollLeftTmp = Math.max(scrollLeft - distance, 0);
    listContainerRef.current?.scrollTo({
      left: scrollLeftTmp,
      behavior: 'smooth',
    });
    updateSlideDisabledState(scrollLeftTmp);
  };

  // 右滑动
  const handleSlideRight = () => {
    if (slideDisabledState.next) return;
    const distance = listContainerWidth.current * distanceRatio;
    const scrollLeft = listContainerRef.current?.scrollLeft || 0;
    const scrollLeftTmp = Math.min(scrollLeft + distance, listOverflowWidth.current);
    listContainerRef.current?.scrollTo({
      left: scrollLeftTmp,
      behavior: 'smooth',
    });
    updateSlideDisabledState(scrollLeftTmp);
  };

  // 滚动条滚动，更新左右滑动按钮的禁用状态
  const handleListContainerScroll = (e: Event) => {
    const node = e?.target;
    const scrollLeftTmp = (node as any)?.scrollLeft || 0;
    updateSlideDisabledState(scrollLeftTmp);
  };

  const { run: handleListContainerScrollRun } = useThrottleFn(handleListContainerScroll, {
    wait: 600,
    leading: true,
    trailing: true,
  });

  const updateIsOverflow = () => {
    if (!containerRef.current) return;
    if (!listContainerRef.current) return;
    const containerWidthTmp = getNodeProps(containerRef.current, NODE_PROPS).width as number;
    const listWidthTmp = getChildrenWidth(listContainerRef.current);
    const isOverflowTmp = getIsOverflow(containerWidthTmp, listWidthTmp, overflowOffset);
    const listContainerWidthTmp = isOverflowTmp ? containerWidthTmp - othersWidth : containerWidthTmp;
    containerWidth.current = containerWidthTmp;
    listContainerWidth.current = listContainerWidthTmp;
    listWidth.current = listWidthTmp;
    listOverflowWidth.current = listWidthTmp - listContainerWidthTmp;
    setIsOverflow(isOverflowTmp);
  };

  const updateSlideDisabledState = (scrollLeft: number) => {
    const slideDisabledStateTmp = getSlideDisabledState(scrollLeft);
    setSlideDisabledState(slideDisabledStateTmp);
  };

  useResizeEffect({
    containerRef,
    listContainerRef,
    updateIsOverflow,
    updateSlideDisabledState,
  });

  useEffect(() => {
    const scrollLeft = listContainerRef.current?.scrollLeft || 0;
    updateIsOverflow();
    updateSlideDisabledState(scrollLeft);

    listContainerRef.current?.addEventListener('scroll', handleListContainerScrollRun, false);

    return () => {
      listContainerRef.current?.removeEventListener('scroll', handleListContainerScrollRun, false);
    };
  }, updateDeps);

  return {
    containerRef,
    listContainerRef,
    isOverflow,
    slideDisabledState,
    handleSlideLeft,
    handleSlideRight,
  };
}
